import * as React from "react";
import { Loader, MiniModal, Spinner, Toast, ZIndex } from "react-ui";
import pLimit from "p-limit";
import areEqual from "fbjs/lib/areEqual";

import { FormObjectProvider } from "../../FormObjectProvider";
import { ExternalLayoutEventsMerger } from "../../ExternalLayoutEventsMerger";
import { LocalizerProvider } from "../../../Engine/Core/Localization/LocalizerContext";
import { IForm } from "../../../Engine/Form/IForm";
import { useAsyncEffect } from "../../../../../Common/UseAsyncEffect";
import { isNullOrWhiteSpace } from "../../../../../Common/TypingUtils";
import { RawFormResources, ReloadableRawFormResources } from "../../../Forms/RawFormResources";
import { candyInjectableModules as allModules } from "../../../Forms/AllModules";
import { CandyThemeWrapper } from "../../../Forms/CandyThemeWrapper";
import { getCandyHub } from "../../../Forms/Sentry/InitializeSentry";
import { CandyInjectableModules } from "../../../Forms/CandyInjectableModules";
import { FormResources, ReloadableFormResources } from "../../../Forms/FormResources";
import { ProgressBar } from "../../Layout/ProgressBar/ProgressBar";
import { delay } from "../../../../../Common/AsyncUtils";
import { isReadOnlyViaQueryString } from "../../../Forms/FormInitialization/PrepareSettingsAndFeatureFlags";
import { ErrorText } from "../../../Engine/Core/ModalManager/Modals/ZeroReporting/ZeroReporting.styles";

import { useInitForm } from "./useInitForm";
import {
    AttachmentNormalizationInfoWrapper,
    NormalizationModalHeader,
    NormalizationModalInfo,
} from "./FormApplication.styles";

const PROGRESS_BAR_SHOW_DELAY = 500;

interface FormApplicationProps {
    children: (allModules: CandyInjectableModules) => React.ComponentType;
    resources: RawFormResources;
    onFormCreated: (form: IForm) => void;
    onCreateFormError: (error: Error) => void;
    onFormDestroy: () => void;
}

export function clearURLQueryParam(name: string) {
    const currentUrlParams = new URLSearchParams(window.location.search);
    currentUrlParams.delete(name);
    if (window.top) {
        // Если форма рисуется в iframe
        const url = window.top.location.pathname + "?" + currentUrlParams.toString();
        window.top.history.replaceState({}, "", url);
    }
    const url = window.location.pathname + "?" + currentUrlParams.toString();
    window.history.replaceState({}, "", url);
}

const hotUpdateLimit = pLimit(1);

export const FormApplication = (props: FormApplicationProps) => {
    const { onFormCreated, onFormDestroy, resources, children } = props;
    const [FormComponent, setFormComponent] = React.useState(() => children(allModules));

    React.useEffect(() => {
        getCandyHub().configureScope(scope => {
            scope.setUser({
                id: resources.settings.userId,
            });
            scope.setTag("gfv", resources.settings.gfv);
            scope.setTag("draft.id", resources.settings.draftId);
            scope.setTag("draft.ns", resources.settings.ns ?? "none");
            scope.setTag("resourceZone", resources.settings.resourceZone ?? "default");
            scope.setTag("theme", resources.settings.theme ?? "default");
        });
    }, []);

    const [formResources, setFormResources] = React.useState<FormResources>(() => {
        const { calculationHooks, calculationFunctions } = resources.calculationFunctions(allModules);
        return {
            ...resources,
            dataDeclaration: resources.dataDeclaration(allModules),
            lazyLoadDeclaration: resources.lazyLoadDeclaration(allModules),
            calculationHooks: calculationHooks,
            calculationFunctions: calculationFunctions,
            validationFunctions: resources.validationFunctions(allModules),
        };
    });

    const { form, normalizationProgress, formInitError } = useInitForm(formResources);

    if (form != undefined) {
        onFormCreated(form);
    }

    if (formInitError != undefined) {
        props.onCreateFormError(formInitError);
    }

    const [validationsAsString, setValidationsAsString] = React.useState(() => {
        if (process.env.NODE_ENV === "development") {
            return resources.validationFunctions.toString();
        } else {
            return "NONE";
        }
    });
    const [calculationFunctionsAsString, setCalculationFunctionsAsString] = React.useState(() => {
        if (process.env.NODE_ENV === "development") {
            return resources.calculationFunctions.toString();
        } else {
            return "NONE";
        }
    });
    const [autocalculationConfiguratorsAsString, setAutocalculationConfiguratorsAsString] = React.useState(() => {
        if (process.env.NODE_ENV === "development") {
            return (resources.autocalculationConfigurators ?? []).map(x => x.toString()).join("");
        } else {
            return "NONE";
        }
    });

    React.useEffect(() => {
        if (form) {
            if (process.env.NODE_ENV === "development") {
                form.updateFormResources = async (
                    nextFormCreator: (allModules: CandyInjectableModules) => React.ComponentType,
                    nextRawResources: ReloadableRawFormResources
                ) => {
                    Toast.push("Обновление ресурсов...");
                    try {
                        setFormComponent(() => nextFormCreator(allModules));
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.error(e);
                    }
                    await hotUpdateLimit(async () => {
                        const updatedCaptions = [];
                        const nextResources: ReloadableFormResources = {};
                        if (!areEqual(nextRawResources.requisiteList, formResources.requisiteList)) {
                            nextResources.requisiteList = nextRawResources.requisiteList;
                            updatedCaptions.push(["список реквизитов"]);
                        }
                        if (nextRawResources.validationFunctions.toString() != validationsAsString) {
                            setValidationsAsString(nextRawResources.validationFunctions.toString());
                            nextResources.validationFunctions = nextRawResources.validationFunctions(allModules);
                            updatedCaptions.push(["валидации"]);
                        }
                        if (
                            (nextRawResources.autocalculationConfigurators ?? []).map(x => x.toString()).join("") !=
                            autocalculationConfiguratorsAsString
                        ) {
                            setAutocalculationConfiguratorsAsString(
                                (nextRawResources.autocalculationConfigurators ?? []).map(x => x.toString()).join("")
                            );
                            nextResources.autocalculationConfigurators = nextRawResources.autocalculationConfigurators;
                            updatedCaptions.push(["КС валидации и авторасчёты"]);
                        }
                        if (nextRawResources.calculationFunctions.toString() != calculationFunctionsAsString) {
                            setCalculationFunctionsAsString(nextRawResources.calculationFunctions.toString());
                            const { calculationHooks, calculationFunctions } =
                                resources.calculationFunctions(allModules);
                            nextResources.calculationHooks = calculationHooks;
                            nextResources.calculationFunctions = calculationFunctions;
                            updatedCaptions.push(["старые авторасчёты"]);
                        }
                        nextResources.resourcesHash = resources.resourcesHash;
                        await form.replaceResources(formResources, nextResources);
                        setFormResources({
                            ...formResources,
                            ...nextResources,
                        });
                        if (updatedCaptions.length > 0) {
                            Toast.push("Обновлены: " + updatedCaptions.join(", ") + ".");
                        } else {
                            Toast.push("Обновление завершено");
                        }
                    });
                };
            }
            form.unmount = onFormDestroy;
        }
    }, [form, onFormDestroy]);

    useAsyncEffect(async () => {
        if (form) {
            const navigationUrl = form.settings.navigationQueryPath || form.settings.navigationPath;
            const navigationAttachmentId = form.settings.navigationAttachmentId;
            const navigationHighlanderPath = form.settings.navigationHighlanderPath;

            if (navigationAttachmentId) {
                if (navigationAttachmentId !== form.settings.attachmentId) {
                    const attachmentPath = form.getAttachmentPath(navigationAttachmentId);

                    if (attachmentPath) {
                        await form.activeLogHelper.navigateByHighlanderPaths([attachmentPath]);
                    }

                    return;
                }

                if (navigationHighlanderPath) {
                    await form.activeLogHelper.navigateByHighlanderPaths([navigationHighlanderPath]);

                    return;
                }
            }

            if (!isNullOrWhiteSpace(navigationUrl)) {
                const isCandyPath = form.navigationPathMapper.isCandyPath(navigationUrl);
                if (isCandyPath) {
                    const url = navigationUrl.startsWith("/") ? navigationUrl.slice(1) : navigationUrl;
                    const pathsToClear = ["navigationPath", "navigationQueryPath"];

                    if (navigationAttachmentId) {
                        pathsToClear.push("navigationAttachmentId");
                    }

                    pathsToClear.map(clearURLQueryParam);
                    await form.activeLogHelper.navigate(url);
                } else {
                    if (form.activeLogHelper != undefined) {
                        await form.activeLogHelper.navigate(navigationUrl);
                    }
                }
            }
        }
    }, [form]);

    const isAttachment = formResources.settings.attachmentId != undefined;
    const formIsLoading = form == undefined;
    const hideFormLoader = resources.settings.hideFormLoader;

    const [normalizationInProgress, setNormalizationInProgress] = React.useState(false);
    useAsyncEffect(async () => {
        const hasNormalizationProgress =
            formIsLoading && normalizationProgress != undefined && normalizationProgress.status !== "Success";

        if (hasNormalizationProgress) {
            setNormalizationInProgress(true);
        }

        if (normalizationInProgress && !hasNormalizationProgress) {
            await delay(PROGRESS_BAR_SHOW_DELAY);
            setNormalizationInProgress(false);
        }
    }, [formIsLoading, normalizationProgress, normalizationInProgress]);

    const renderContent = () => {
        const isReadOnly = isReadOnlyViaQueryString();
        const normalizationFailed = normalizationProgress?.status === "Error";

        if (isAttachment && normalizationProgress != undefined && normalizationInProgress) {
            return (
                <AttachmentNormalizationInfoWrapper>
                    {!normalizationFailed ? (
                        <>
                            <NormalizationModalInfo>
                                <div>Подготовка раздела к работе. Пожалуйста, подождите</div>
                                <div>{Math.floor(normalizationProgress.progress ?? 0)}%</div>
                            </NormalizationModalInfo>
                            <ProgressBar progress={normalizationProgress.progress} />
                        </>
                    ) : (
                        <NormalizationModalInfo>
                            <ErrorText>
                                Не удалось загрузить раздел.
                                <br />
                                Повторите попытку через несколько минут. Если ошибка повторяется, напишите в
                                техподдержку.
                            </ErrorText>
                        </NormalizationModalInfo>
                    )}
                </AttachmentNormalizationInfoWrapper>
            );
        }

        if (isAttachment && form == undefined) {
            return <Loader active={true} caption={"Загружаем раздел"} />;
        }

        if (formIsLoading && hideFormLoader) {
            return null;
        }

        if (normalizationProgress != undefined && normalizationInProgress) {
            return (
                <MiniModal width={600} ignoreBackgroundClick={true}>
                    <MiniModal.Header icon={null}>
                        <NormalizationModalHeader>
                            Подготовка {isReadOnly ? "документа" : "черновика"} к работе
                        </NormalizationModalHeader>
                    </MiniModal.Header>
                    <MiniModal.Body>
                        {!normalizationFailed ? (
                            <>
                                <NormalizationModalInfo>
                                    <div>Пожалуйста, подождите</div>
                                    <div>{Math.floor(normalizationProgress.progress ?? 0)}%</div>
                                </NormalizationModalInfo>
                                <ProgressBar progress={normalizationProgress.progress} />
                            </>
                        ) : (
                            <NormalizationModalInfo>
                                <ErrorText>
                                    Не удалось загрузить {isReadOnly ? "документ" : "черновик"}.
                                    <br />
                                    Повторите попытку через несколько минут. Если ошибка повторяется, напишите в
                                    техподдержку.
                                </ErrorText>
                            </NormalizationModalInfo>
                        )}
                    </MiniModal.Body>
                </MiniModal>
            );
        }

        if (formIsLoading) {
            return (
                <MiniModal ignoreBackgroundClick={true}>
                    <MiniModal.Body>
                        <Spinner caption={`Загружаем ${isReadOnly ? "документ" : "черновик"}`} type="big" />
                    </MiniModal.Body>
                </MiniModal>
            );
        }

        return (
            <FormObjectProvider form={form}>
                <ExternalLayoutEventsMerger externalLayoutEvents={resources.settings.eventEmitter}>
                    <LocalizerProvider localizer={form.localization}>
                        {form.settings.setModalContext ? (
                            <ModalContextWrapper>
                                <FormComponent data-tid="LoadedFormComponent" />
                            </ModalContextWrapper>
                        ) : (
                            <FormComponent data-tid="LoadedFormComponent" />
                        )}
                    </LocalizerProvider>
                </ExternalLayoutEventsMerger>
            </FormObjectProvider>
        );
    };

    return <CandyThemeWrapper theme={resources.settings.theme}>{renderContent()}</CandyThemeWrapper>;
};

/**
 * Костыль для корректного отображения DropDown и прочих портальных элементов внутри модалки
 */
function ModalContextWrapper(props: { children: React.ReactNode }): JSX.Element {
    return (
        <ZIndex priority="Modal">
            <>{props.children}</>
        </ZIndex>
    );
}
